import { autocompletion, CompletionContext } from '@codemirror/autocomplete'
import { AttachMoneyRounded, DeleteRounded, PercentRounded } from '@mui/icons-material'
import { Box, IconButton, MenuItem, Stack, TextField, ToggleButton } from '@mui/material'
import { ChangeEvent, memo } from 'react'
import { INode, NodeType } from '../../../../../definitions/Nodes'
import { NodeDataMap } from '../../../../../definitions/NodeTypes'
import NodeCodemirrorManager from '../../../../../managers/NodeCodemirrorManager'
import { IShapeInput } from '../../../../../managers/NodeManager'
import NodeCodemirrorTextEditor from '../../NodeCodemirrorTextEditor'

interface P {
    setSelected: React.Dispatch<React.SetStateAction<INode<'stmt_settlement'>>>
    shapes: IShapeInput[]
    disable: boolean
    item: NodeDataMap['stmt_settlement']['itemsMap'][0]
    idx: number
    inputNodes: INode<NodeType>[]
}

const MappingResult = ({ setSelected, shapes, disable, item, idx, inputNodes }: P) => {
    const shape = shapes.find(s => s.nodeUuid === item.fromInputNode)?.shape
    const keywords = shape ? NodeCodemirrorManager.generateCompletionsFromShape(shape) : []

    const customCompletions = (context: CompletionContext) => {
        const word = context.matchBefore(/\w*/)
        if (word.from === word.to && !context.explicit) return null
        return {
            from: word.from,
            options: keywords
        }
    }

    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, idx: number) => {
        const { name, value } = e.target
        setSelected(prev => {
            const newItemsMap = [...prev.data.itemsMap]
            newItemsMap[idx] = {
                ...newItemsMap[idx],
                [name]: value
            }
            return { ...prev, data: { ...prev.data, itemsMap: newItemsMap } }
        })
    }

    const handleDelete = (idx: number) => {
        setSelected(prev => {
            const newItemsMap = [...prev.data.itemsMap]
            newItemsMap.splice(idx, 1)
            return { ...prev, data: { ...prev.data, itemsMap: newItemsMap } }
        })
    }

    const handleFormatChange = (idx: number, format: 'percent' | 'financial') => {
        setSelected(prev => {
            const newItemsMap = [...prev.data.itemsMap]
            const current = newItemsMap[idx].format
            newItemsMap[idx] = {
                ...newItemsMap[idx],
                format: current === format ? null : format
            }
            return { ...prev, data: { ...prev.data, itemsMap: newItemsMap } }
        })
    }

    return (
        <Stack spacing={1}>
            <Stack direction='row' gap={2} flex={1}>
                <Stack direction='row' width='25%' flex={1}>
                    <TextField
                        name='settlementOutputKey'
                        fullWidth
                        size='small'
                        disabled={disable}
                        value={item?.settlementOutputKey ?? ''}
                        onChange={e => handleChange(e, idx)}>
                    </TextField>
                </Stack>
                <Stack direction='row' width='25%' flex={1}>
                    <TextField
                        name='fromInputNode'
                        fullWidth
                        select
                        size='small'
                        disabled={disable}
                        value={item.fromInputNode ?? ''}
                        onChange={e => handleChange(e, idx)}>
                        {inputNodes.map(option => (
                            <MenuItem key={option.uuid} value={option.uuid}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
                <Stack direction='row' gap={1} width='25%' flex={1}>
                    <NodeCodemirrorTextEditor
                        value={item.fromInputNodeOutputKey ?? ''}
                        onChange={v => {
                            setSelected(prev => {
                                const newItemsMap = [...prev.data.itemsMap]
                                newItemsMap[idx] = {
                                    ...newItemsMap[idx],
                                    fromInputNodeOutputKey: v
                                }
                                return { ...prev, data: { ...prev.data, itemsMap: newItemsMap } }
                            })
                        }}
                        disabled={disable}
                        extensions={[autocompletion({ override: [customCompletions] })]}
                    />
                </Stack>
                <Stack direction='row' gap={1} flex={1} alignItems='center' width='25%' justifyContent='space-between'>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <ToggleButton
                            size='small'
                            value='percent'
                            selected={item.format === 'percent'}
                            onChange={() => handleFormatChange(idx, 'percent')}
                            disabled={disable}>
                            <PercentRounded fontSize='small' />
                        </ToggleButton>
                        <ToggleButton
                            size='small'
                            value='financial'
                            selected={item.format === 'financial'}
                            onChange={() => handleFormatChange(idx, 'financial')}
                            disabled={disable}>
                            <AttachMoneyRounded fontSize='small' />
                        </ToggleButton>
                    </Box>
                    <Box>
                        <IconButton
                            size='small'
                            onClick={() => handleDelete(idx)}
                            disabled={disable}>
                            <DeleteRounded color={disable ? undefined : 'error'} />
                        </IconButton>
                    </Box>
                </Stack>
            </Stack>
        </Stack>
    )
}

export default memo(MappingResult)
